import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';

import { env } from '@/constants/env';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useThemeStore } from '@/store/theme.store';
import { useUiStore } from '@/store/ui.store';

export const TOP_BAR_HEIGHT = 64;

export function TopBar() {
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const isDark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  function handleOpenMenu(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setAnchorEl(null);
  }

  async function handleLogout() {
    handleCloseMenu();
    await logout();
    void navigate('/login', { replace: true });
  }

  return (
    <AppBar
      position="fixed"
      color="inherit"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper' }}
    >
      <Toolbar sx={{ height: TOP_BAR_HEIGHT, gap: 1 }}>
        <IconButton edge="start" onClick={toggleSidebar} aria-label="Toggle navigation">
          <MenuIcon />
        </IconButton>

        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {env.VITE_APP_NAME}
        </Typography>

        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            onClick={() => {
              setMode(isDark ? 'light' : 'dark');
            }}
            aria-label="Toggle color mode"
          >
            {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Account">
          <IconButton onClick={handleOpenMenu} aria-label="Account menu" size="small" sx={{ ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{user?.name.charAt(0).toUpperCase() ?? '?'}</Avatar>
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu} onClick={handleCloseMenu}>
          <Box px={2} py={1} minWidth={200}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              void handleLogout();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
